import { useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { Lifebuoy } from "@phosphor-icons/react"
import { t } from "i18next"
import z from "zod"
import { getUser } from "@/apis/bff"
import { fetchLatestReleaseInfo } from "@/apis/release-info"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTrigger,
} from "@/components/ui/dialog"
import { TextArea } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useConfirmBeforeLeaving } from "@/hooks/useConfirmBeforeLeaving"
import { toast } from "@/hooks/useToast"
import { useReportAnIssueMutation } from "@/modules/common/report-issue/services/useReportAnIssueMutation"
import { StaleVersionNotice } from "@/modules/common/report-issue/stale-version-notice"
import { SofiaEnvironment } from "@/sofia-environment"
import { faroEventStore } from "@/stores/faro-event-store"
import { queriesStore } from "@/stores/queries-store"
import { useVersionStore } from "@/stores/useVersionStore"

const reportIssueSchema = z.object({
  issueDescription: z
    .string()
    .min(50, t("common.fieldErrorList.issueDescriptionMinLength", { min: 50 }))
    .max(5000, t("common.fieldErrorList.maxLengthField", { max: 5000 })),
  stepsDescription: z
    .string()
    .min(30, t("common.fieldErrorList.stepsDescriptionMinLength", { min: 30 }))
    .max(5000, t("common.fieldErrorList.maxLengthField", { max: 5000 })),
})

type ReportIssueForm = z.infer<typeof reportIssueSchema>

export const ReportIssue = ({ open }: { open?: boolean }) => {
  const [modalOpen, setModalOpen] = useState(open)
  const [staleNoticeOpen, setStaleNoticeOpen] = useState(false)
  const { t } = useTranslation()
  const isStaleVersion = useVersionStore((state) => state.isStaleVersion)

  const handleOpenChange = (isOpening: boolean) => {
    if (isOpening && !open && isStaleVersion) {
      setStaleNoticeOpen(true)
      return
    }
    setModalOpen(isOpening)
  }

  const form = useForm<ReportIssueForm>({
    resolver: zodResolver(reportIssueSchema),
    defaultValues: {
      issueDescription: "",
      stepsDescription: "",
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form
  const [reportIssue, isReportingIssue] = useReportAnIssueMutation()

  const handleSendReportIssue: SubmitHandler<ReportIssueForm> = async (data) => {
    try {
      const issueData = await collectIssueData(data)

      reportIssue({
        variables: {
          input: {
            issueDescription: issueData.issueDescription,
            actionsBeforeIssue: issueData.stepsDescription,
            consoleLogJson: btoa(JSON.stringify(issueData.consoleOutput)),
            currentLocation: issueData.currentUrl,
            injectedSofiaEnvironmentJson: btoa(JSON.stringify(issueData.sofiaEnvironmentData)),
            fetchedUserInfoJson: btoa(JSON.stringify(issueData.userInfo.raw)),
            parsedUserInfoJson: btoa(JSON.stringify(issueData.userInfo.parsed)),
            releaseInfoJson: btoa(JSON.stringify(issueData.releaseInfo)),
            userAgent: issueData.userAgent,
            latestFrontEndErrorsJson: btoa(JSON.stringify(issueData.latestFrontEndErrorsJson)),
            graphQlLogJson: btoa(JSON.stringify(issueData.queries)),
            faroInsightJson: btoa(JSON.stringify(issueData.faroData)),
            windowSize: {
              innerWidth: issueData.windowSize.width,
              innerHeight: issueData.windowSize.height,
            },
          },
        },
        onCompleted: (response) => {
          if (response.reportAnIssue.boolean) {
            if (open) {
              window.location.reload()
            }
            setModalOpen(false)
            form.reset()
            toast({ description: t("reportIssue.successMessage") })
          } else {
            console.error("Failed to report issue")
          }
        },
        onError: (error) => {
          console.error("Error reporting issue:", error)
        },
      })
    } catch (error) {
      console.error("Failed to collect issue data:", error)
    }
  }

  const collectIssueData = async ({ issueDescription, stepsDescription }: ReportIssueForm) => {
    // Get console output from the intercepted console buffer
    const consoleOutput = window.console.getBuffer()

    const latestFrontEndErrorsJson = window.console
      .getBuffer()
      .filter((log) => log.level === "error")
      .slice(-5)

    // Get injected Sofia environment data
    const sofiaEnvironmentData = SofiaEnvironment

    // Get user information (both raw and parsed)
    const { user, rawUserAttributes } = await getUser()

    const rawUserAttributesAsObject = rawUserAttributes.reduce<Record<string, string | number | (string | number)[]>>(
      (acc, pair) => {
        const { type, value } = pair
        if (acc[type]) {
          if (Array.isArray(acc[type])) {
            acc[type].push(value)
          } else {
            acc[type] = [acc[type], value]
          }
        } else {
          acc[type] = value
        }
        return acc
      },
      {},
    )
    const userInfo = {
      raw: rawUserAttributesAsObject,
      parsed: user,
    }

    // Get release info data
    let releaseInfo
    try {
      releaseInfo = await fetchLatestReleaseInfo()
    } catch (error) {
      console.error("Failed to fetch release info:", error)
      releaseInfo = {}
    }

    const currentUrl = window.location.href

    const { userAgent } = navigator

    const windowSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    }

    const queries = queriesStore.getAllQueries()

    // Serialize queries with proper error handling
    const serializedQueries = queries.map((query) => ({
      ...query,
      error:
        query.error instanceof Error
          ? {
              name: query.error.name,
              message: query.error.message,
              stack: query.error.stack,
            }
          : query.error,
    }))

    const faroData = faroEventStore.getAllEvents()

    return {
      issueDescription,
      stepsDescription,
      consoleOutput,
      sofiaEnvironmentData,
      userInfo,
      releaseInfo,
      currentUrl,
      userAgent,
      windowSize,
      latestFrontEndErrorsJson,
      queries: serializedQueries,
      faroData,
    }
  }

  useConfirmBeforeLeaving(true)

  return (
    <>
      <StaleVersionNotice open={staleNoticeOpen} />
      <Dialog open={modalOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            data-testid="report-issue-button"
            variant="tertiary"
            className="flex h-8 w-full justify-start rounded-md px-2 py-1 leading-5 font-normal">
            <Lifebuoy size={20} />
            <span>{t("common.button.reportAnIssue")}</span>
          </Button>
        </DialogTrigger>
        <DialogPortal>
          <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
          <DialogContent
            variant="small"
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <div className="p-8 pb-0 text-xl font-medium">
                {open ? `${t("common.errorOccurred")}. ${t("reportIssue.title")}` : t("reportIssue.title")}
              </div>
              {open && (
                <p className="text-md text-on-surface-subdued px-8 pb-4">{t("reportIssue.autoReloadMessage")}</p>
              )}
            </DialogHeader>
            <ScrollArea viewportClassName="max-h-150">
              <div className="flex flex-col gap-4 overflow-y-auto p-6">
                <FormProvider {...form}>
                  <div>
                    <TextArea
                      className="max-h-56"
                      textLimit={5000}
                      label={t("reportIssue.issueDescriptionLabel")}
                      id="issue-description"
                      {...register("issueDescription")}
                      error={errors.issueDescription?.message}
                    />
                  </div>
                  <div>
                    <TextArea
                      className="max-h-56"
                      textLimit={5000}
                      label={t("reportIssue.stepsDescriptionLabel")}
                      id="steps-description"
                      {...register("stepsDescription")}
                      error={errors.stepsDescription?.message}
                    />
                  </div>
                </FormProvider>
              </div>
            </ScrollArea>
            <DialogFooter className="flex border-t">
              {!open && (
                <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={isReportingIssue}>
                  {t("common.button.cancel")}
                </Button>
              )}
              <Button onClick={handleSubmit(handleSendReportIssue)} disabled={isReportingIssue}>
                {isReportingIssue
                  ? t("reportIssue.sending")
                  : open
                    ? `${t("reportIssue.sendButton")} & ${t("reportIssue.refreshPage")}`
                    : t("reportIssue.sendButton")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  )
}
